import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
  Photo,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  FaAws,
  FaFileExcel,
  FaDownload,
  FaCloud,
  FaImages,
  FaHdd
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import RelatedToolsBar from '../components/RelatedToolsBar';
import { TOOL_COLORS } from '../constants/colors';

// Dimensions per size, mirroring ImageResizeHelper.DimensionsForSize on the
// backend. Original is the source image's own size (no resize).
const SIZE_DIMENSION_LABELS = {
  Original:  'Source dimensions (no resize)',
  Zoom:      '1200 × 1200 px',
  Large:     '600 × 600 px',
  Small:     '300 × 300 px',
  Thumbnail: '150 × 150 px',
};

const AWSImageLinksAlternate = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Mode toggle: rugs or furniture
  const [mode, setMode] = useState('rugs');

  // API base path depends on mode
  const apiPath = mode === 'rugs' ? 'AWSImageLinksJeff' : 'AWSImageLinksJeffFurniture';

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [file, setFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showLogic, setShowLogic] = useState(false);

  // ─── Resize options ──────────────────────────────────────────────────
  // Match the AWS File Links "Excel Import" tab UX. The selected size
  // determines which dimensions the output images are resized to (or which
  // pre-generated S3 folder the URL points at, when neither Square nor
  // Max-KB is on). Square forces the canvas to a 1:1 aspect with white
  // padding. Max-KB iteratively lowers JPEG quality until the file fits.
  // Multi-select image sizes. Each true entry produces its own sheet in
  // the output Excel — Original keeps the existing URLs, others swap
  // the S3 folder (or run the resize pipeline if Square/custom/maxKb is on).
  const [imageSizes, setImageSizes] = useState({
    Original: true,
    Zoom: false,
    Large: false,
    Small: false,
    Thumbnail: false,
  });
  const toggleImageSize = (key) =>
    setImageSizes((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // Never let the user deselect everything — silently re-enable
      // Original so submitting always produces at least one sheet.
      if (!Object.values(next).some(Boolean)) next.Original = true;
      return next;
    });
  // Square mode replaces the simple boolean — "none" / "square" /
  // "squareBorder" (the last one adds a 15% white margin like the legacy
  // chkSquareWithWhiteBorder option from the WinForms tool).
  const [squareMode, setSquareMode] = useState('none');
  // Optional custom dimensions. When either is set, it overrides the
  // preset size. Leaving height blank mirrors width (and vice versa) so a
  // single value still produces a square output.
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  // Max-KB modes: 'off' (no limit), 'preset' (chip-selected number),
  // 'custom' (free-form number). Stored separately so the UI can remember
  // both preset and custom values when toggling between modes.
  const [maxKbMode, setMaxKbMode] = useState('off');
  const [maxKbPreset, setMaxKbPreset] = useState(400);
  const [maxKbCustom, setMaxKbCustom] = useState('');
  // Computed effective max-KB to send to the backend. 0 means "no limit".
  const effectiveMaxKb = (() => {
    if (maxKbMode === 'off') return 0;
    if (maxKbMode === 'preset') return maxKbPreset;
    const parsed = parseInt(maxKbCustom, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/api/${apiPath}/status/${processId}`);

      if (response.logs && response.logs.length > 0) {
        setLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName, response.awsUrl);
        }

        setResults({
          success: true,
          fileCount: response.processedItems || 0,
          fileName: response.fileName,
          missingImageCount: response.missingImageCount || 0,
          uploadScriptUrl: response.uploadScriptUrl || null
        });
        completeActivityProcess(processId, 'success', 'Processing completed successfully');
        setSuccess(response.missingImageCount > 0
          ? `Processing complete! ${response.missingImageCount} images were not found on S3 — download the upload script to fix.`
          : 'Processing complete! Excel file with AWS image links has been generated.');
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName, awsUrl) => {
    try {
      addLog(processId, 'Downloading file...');

      // Primary: download from S3 URL (survives app pool recycles)
      if (awsUrl) {
        const link = document.createElement('a');
        link.href = awsUrl;
        link.download = serverFileName || `${mode === 'rugs' ? 'Rugs' : 'Furniture'} AWS Image Link Result ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        addLog(processId, 'Download complete!');
        return;
      }

      // Fallback: download from in-memory endpoint
      const response = await fetch(`${API_BASE_URL}/api/${apiPath}/download/${processId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || `${mode === 'rugs' ? 'Rugs' : 'Furniture'} AWS Image Link Result ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${apiPath}/sample`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download sample file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      link.download = `AWS Image Links Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download sample file.');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const droppedFile = droppedFiles[0];
    if (droppedFile && droppedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop an Excel or CSV file only (.xlsx, .xls, .csv)');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setError('Please select an Excel or CSV file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', file);
      // Resize options — backend treats default (Original + squareMode=none +
      // no custom dims + maxKb=0) as the fast path (just reuse existing S3
      // URLs, no download/resize). Any other combination triggers the full
      // download → resize → re-upload pipeline.
      // Multi-select sizes → comma-separated string. Server falls back to
      // imageSize if imageSizes is empty, so we still send a single-size
      // value for back-compat.
      // When custom W/H is set, the preset sizes are disabled in the UI but
      // any previously-checked entries are still in state. Collapse to a
      // single sheet so we don't emit N identical sheets.
      const customActive = customWidth !== '' || customHeight !== '';
      const allSelected = Object.keys(imageSizes).filter((k) => imageSizes[k]);
      const selectedSizesList = customActive
        ? [allSelected[0] || 'Original']
        : allSelected;
      formData.append('imageSize',  selectedSizesList[0] || 'Original');
      formData.append('imageSizes', selectedSizesList.join(','));
      formData.append('squareMode', squareMode);
      formData.append('customWidth',  String(parseInt(customWidth,  10) || 0));
      formData.append('customHeight', String(parseInt(customHeight, 10) || 0));
      formData.append('maxKb', String(effectiveMaxKb));

      const response = await apiCall(`${API_BASE_URL}/api/${apiPath}/process`, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, `Processing AWS File Links Alternate (${mode === 'rugs' ? 'Rugs' : 'Furniture'})`, 'AWS File Links Alternate');
        addLog(serverProcessId, 'Starting processing...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  // Image upload handlers for A:\ drive images
  const handleImageDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingImages(true); };
  const handleImageDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingImages(false); };
  const handleImageDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImages(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.name.toLowerCase().match(/\.(jpg|jpeg|png|tif|tiff|bmp|gif|webp)$/)
    );
    if (dropped.length > 0) {
      setImageFiles(prev => [...prev, ...dropped]);
      setError('');
    } else {
      setError('Please drop image files only (.jpg, .png, .tif, etc.)');
    }
  };

  const handleImageSelect = (event) => {
    const selected = Array.from(event.target.files).filter(f =>
      f.name.toLowerCase().match(/\.(jpg|jpeg|png|tif|tiff|bmp|gif|webp)$/)
    );
    if (selected.length > 0) {
      setImageFiles(prev => [...prev, ...selected]);
      setError('');
    }
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleUploadImages = async () => {
    if (imageFiles.length === 0) return;
    setUploading(true);
    setError('');
    setUploadResult(null);
    try {
      const formData = new FormData();
      imageFiles.forEach(f => formData.append('files', f));
      const response = await apiCall(`${API_BASE_URL}/api/${apiPath}/upload-images`, {
        method: 'POST',
        body: formData
      });
      setUploadResult(response);
      setSuccess(`Uploaded ${response.uploaded} images to S3 (${response.skipped} skipped)`);
      setImageFiles([]);
    } catch (err) {
      setError(err.message || 'Failed to upload images');
    }
    setUploading(false);
  };

  const resetForm = () => {
    setFile(null);
    setImageFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="AWS File Links Alternate" category="link" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          AWS FILE LINKS ALTERNATE
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Process
              </Typography>

              {/* Mode Toggle */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Mode:
                </Typography>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(e, newMode) => { if (newMode) { setMode(newMode); resetForm(); } }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: TOOL_COLORS.LINK.primary,
                        color: '#fff',
                        '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                      },
                    },
                  }}
                >
                  <ToggleButton value="rugs">Rugs</ToggleButton>
                  <ToggleButton value="furniture">Furniture</ToggleButton>
                </ToggleButtonGroup>
                <Chip
                  label={mode === 'rugs' ? 'Size interchange + shape filtering' : 'BED handling + F/Q/K/T filtering'}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                {mode === 'rugs'
                  ? 'Upload an Excel or CSV file with rug Item IDs in the first column. The tool uses size interchange groups and shape filtering (round/oval vs square/rectangle) to find matching product images, uploads them to AWS S3, and generates an Excel file with S3 links organized by image type columns.'
                  : 'Upload an Excel or CSV file with furniture Item IDs in the first column. The tool supports BED item handling and F/Q/K/T suffix exclusion filtering, finds matching product images, uploads them to AWS S3, and generates an Excel file with S3 links organized by image type columns.'}
              </Typography>

              {/* Resize options — image size + Square + Max-KB cap. Mirrors
                  the AWS File Links Excel Import tab. Any combination other
                  than Original + Square=off + Max-KB=0 routes through the
                  backend's download-resize-reupload pipeline. */}
              <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
                <CardContent>
                  <Grid container spacing={2.5} alignItems="flex-start">
                    {/* Image sizes — multi-select. Each ticked size
                        becomes its own sheet in the output Excel. */}
                    <Grid item xs={12} md={5}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                          Image Sizes
                        </FormLabel>
                        <FormGroup row sx={{ gap: 0.5 }}>
                          {Object.keys(imageSizes).map((size) => {
                            const customActive = customWidth !== '' || customHeight !== '';
                            const tip = customActive
                              ? 'Disabled — custom W/H dimensions override the preset sizes. Clear them to use sizes again.'
                              : SIZE_DIMENSION_LABELS[size] || '';
                            return (
                              <Tooltip key={size} title={tip} arrow placement="top" disableInteractive>
                                <span>
                                  <FormControlLabel
                                    disabled={customActive}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={imageSizes[size]}
                                        onChange={() => toggleImageSize(size)}
                                        sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }}
                                      />
                                    }
                                    label={<Typography variant="body2">{size}</Typography>}
                                  />
                                </span>
                              </Tooltip>
                            );
                          })}
                        </FormGroup>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                          {(customWidth !== '' || customHeight !== '')
                            ? 'Sizes disabled — custom W/H is driving the output.'
                            : 'One sheet per size. Non-Original sizes swap the S3 folder.'}
                        </Typography>
                      </FormControl>
                    </Grid>

                    {/* Square style — none / square / square + white border */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                          Square Style
                        </FormLabel>
                        <RadioGroup
                          value={squareMode}
                          onChange={(e) => setSquareMode(e.target.value)}
                          sx={{ gap: 0.25 }}
                        >
                          {[
                            { value: 'none',         label: 'None' },
                            { value: 'square',       label: 'Square' },
                            { value: 'squareBorder', label: 'Square + Border' },
                          ].map((opt) => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                              label={<Typography variant="body2">{opt.label}</Typography>}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Custom dimensions — overrides the preset size when set */}
                    <Grid item xs={12} md={4}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                          Custom Dimensions (px)
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Width"
                            value={customWidth}
                            onChange={(e) => setCustomWidth(e.target.value.replace(/[^\d]/g, ''))}
                            InputProps={{ sx: { fontSize: '0.85rem' } }}
                            sx={{ width: 110 }}
                          />
                          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>×</Typography>
                          <TextField
                            size="small"
                            placeholder="Height"
                            value={customHeight}
                            onChange={(e) => setCustomHeight(e.target.value.replace(/[^\d]/g, ''))}
                            InputProps={{ sx: { fontSize: '0.85rem' } }}
                            sx={{ width: 110 }}
                          />
                          {(customWidth || customHeight) && (
                            <IconButton
                              size="small"
                              onClick={() => { setCustomWidth(''); setCustomHeight(''); }}
                              sx={{ p: 0.5 }}
                            >
                              <Clear sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.75 }}>
                          {customWidth || customHeight
                            ? 'Overrides the preset Image Size. Leave one blank to mirror.'
                            : 'Optional — leave blank to use preset Image Size.'}
                        </Typography>
                      </FormControl>
                    </Grid>

                    {/* Max KB */}
                    <Grid item xs={12} md={5}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                          Max File Size
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Chip
                            label="No limit"
                            size="small"
                            clickable
                            color={maxKbMode === 'off' ? 'primary' : 'default'}
                            variant={maxKbMode === 'off' ? 'filled' : 'outlined'}
                            onClick={() => setMaxKbMode('off')}
                            sx={maxKbMode === 'off' ? { bgcolor: TOOL_COLORS.LINK.primary, color: '#fff' } : {}}
                          />
                          {[400, 600, 1000, 10200].map((kb) => {
                            const selected = maxKbMode === 'preset' && maxKbPreset === kb;
                            return (
                              <Chip
                                key={kb}
                                label={kb >= 1000 ? `${Math.round(kb / 1000)} MB` : `${kb} KB`}
                                size="small"
                                clickable
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => { setMaxKbMode('preset'); setMaxKbPreset(kb); }}
                                sx={selected ? { bgcolor: TOOL_COLORS.LINK.primary, color: '#fff' } : {}}
                              />
                            );
                          })}
                          <TextField
                            size="small"
                            placeholder="Custom"
                            value={maxKbCustom}
                            onFocus={() => setMaxKbMode('custom')}
                            onChange={(e) => {
                              // Only allow positive integers up to a sane bound.
                              const v = e.target.value.replace(/[^\d]/g, '');
                              setMaxKbCustom(v);
                              setMaxKbMode('custom');
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">KB</InputAdornment>,
                              sx: {
                                fontSize: '0.85rem',
                                bgcolor: maxKbMode === 'custom' ? `${TOOL_COLORS.LINK.primary}10` : 'transparent',
                              },
                            }}
                            sx={{
                              width: 110,
                              '& .MuiOutlinedInput-root': maxKbMode === 'custom'
                                ? { '& fieldset': { borderColor: TOOL_COLORS.LINK.primary } }
                                : {},
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.75 }}>
                          {effectiveMaxKb > 0
                            ? `Output images compressed to ≤ ${effectiveMaxKb} KB each.`
                            : 'No file-size limit applied.'}
                        </Typography>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel / CSV File
                    </Typography>
                    {file && (
                      <Tooltip title="1 file selected">
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />

                  {/* Drag & Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.LINK.primary : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.LINK.light
                        : file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : TOOL_COLORS.LINK.primary,
                        bgcolor: file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.LINK.light,
                      }
                    }}
                  >
                    {!file ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging
                            ? 'Drop file here'
                            : 'Drop your Excel or CSV file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) or CSV (.csv) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop to replace
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File Info */}
                  {file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          borderColor: TOOL_COLORS.LINK.primary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({formatFileSize(file.size)})
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Upload Images from A:\ Drive Section */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: showImageUpload ? 2 : 0 }}
                    onClick={() => setShowImageUpload(!showImageUpload)}
                  >
                    <FaHdd color={theme.palette.text.secondary} size={16} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary, flex: 1 }}>
                      Upload Images from Local Drive (A:\)
                    </Typography>
                    <Chip label="Optional" size="small" variant="outlined" sx={{ mr: 1, fontSize: 11 }} />
                    {showImageUpload ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  <Collapse in={showImageUpload}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                      If images are not yet on S3, upload them from your local drive (e.g. A:\) before processing.
                      This replicates the desktop ImageLinks app upload behavior.
                    </Typography>

                    {/* Hidden image input */}
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageSelect}
                      accept=".jpg,.jpeg,.png,.tif,.tiff,.bmp,.gif,.webp"
                      multiple
                      style={{ display: 'none' }}
                    />

                    {/* Image Drop Zone */}
                    <Box
                      onDragEnter={handleImageDragEnter}
                      onDragOver={handleImageDragOver}
                      onDragLeave={handleImageDragLeave}
                      onDrop={handleImageDrop}
                      onClick={() => imageInputRef.current?.click()}
                      sx={{
                        border: `2px dashed ${isDraggingImages ? '#FF9800' : imageFiles.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        bgcolor: isDraggingImages
                          ? alpha('#FF9800', 0.08)
                          : imageFiles.length > 0
                            ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.04)')
                            : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        mb: 2,
                        '&:hover': {
                          borderColor: '#FF9800',
                          bgcolor: alpha('#FF9800', 0.06),
                        }
                      }}
                    >
                      {imageFiles.length === 0 ? (
                        <>
                          <Photo sx={{ fontSize: 36, color: isDraggingImages ? '#FF9800' : 'text.secondary', mb: 0.5 }} />
                          <Typography variant="body2" gutterBottom>
                            {isDraggingImages ? 'Drop images here' : 'Drop image files from A:\\ drive or click to browse'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            JPG, PNG, TIF, BMP, GIF, WebP supported
                          </Typography>
                        </>
                      ) : (
                        <>
                          <FaImages color="#4CAF50" size={28} style={{ marginBottom: 4 }} />
                          <Typography variant="body2" gutterBottom>
                            {imageFiles.length} image{imageFiles.length !== 1 ? 's' : ''} selected
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Click or drop to add more
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* Image file list (show first 5) */}
                    {imageFiles.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {imageFiles.slice(0, 5).map((f, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                            <Photo sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.name}
                            </Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setImageFiles(prev => prev.filter((_, idx) => idx !== i)); }} sx={{ p: 0.2 }}>
                              <Clear sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                        {imageFiles.length > 5 && (
                          <Typography variant="caption" color="text.secondary">
                            ...and {imageFiles.length - 5} more
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Upload button */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleUploadImages(); }}
                        disabled={imageFiles.length === 0 || uploading}
                        sx={{
                          bgcolor: '#FF9800',
                          '&:hover': { bgcolor: '#F57C00' },
                          textTransform: 'none'
                        }}
                        startIcon={<CloudUpload />}
                      >
                        {uploading ? 'Uploading...' : `Upload ${imageFiles.length} Image${imageFiles.length !== 1 ? 's' : ''} to S3`}
                      </Button>
                      {imageFiles.length > 0 && !uploading && (
                        <Button
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setImageFiles([]); }}
                          sx={{ color: 'text.secondary', textTransform: 'none' }}
                        >
                          Clear All
                        </Button>
                      )}
                    </Box>

                    {uploading && (
                      <LinearProgress sx={{ mt: 1, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: '#FF9800' } }} />
                    )}

                    {uploadResult && (
                      <Alert severity="success" sx={{ mt: 1 }} onClose={() => setUploadResult(null)}>
                        {uploadResult.uploaded} uploaded, {uploadResult.skipped} skipped (already on S3 or invalid)
                      </Alert>
                    )}
                  </Collapse>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcess}
                  disabled={!file || loading}
                  sx={{
                    bgcolor: TOOL_COLORS.LINK.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                    px: 4
                  }}
                  startIcon={<FaCloud />}
                >
                  {loading ? 'Processing...' : 'Process'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleDownloadSample}
                  disabled={loading}
                  sx={{
                    borderColor: TOOL_COLORS.LINK.primary,
                    color: TOOL_COLORS.LINK.primary,
                    '&:hover': {
                      borderColor: TOOL_COLORS.LINK.hover,
                      bgcolor: TOOL_COLORS.LINK.light
                    }
                  }}
                  startIcon={<FaDownload />}
                >
                  Download Template
                </Button>

                {(file || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Info Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing file...
                  </Typography>
                  <LinearProgress
                    variant={progress > 0 ? "determinate" : "indeterminate"}
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: TOOL_COLORS.LINK.primary,
                        borderRadius: 4,
                      }
                    }}
                  />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaAws color={TOOL_COLORS.LINK.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.fileCount || 0} Items Processed`}
                        secondary="Images found and uploaded to AWS S3"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFileExcel color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Excel File Generated"
                        secondary="Contains all AWS S3 image links by type"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Excel file has been automatically downloaded. Check your downloads folder.
                  </Alert>

                  {results.missingImageCount > 0 && (
                    <Alert
                      severity="warning"
                      sx={{ mt: 2 }}
                      action={
                        results.uploadScriptUrl ? (
                          <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = results.uploadScriptUrl;
                              link.download = 'Upload_Missing_Images.ps1';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            startIcon={<FaDownload size={12} />}
                          >
                            Download Script
                          </Button>
                        ) : null
                      }
                    >
                      <strong>{results.missingImageCount} images</strong> were in the database but not found on S3.
                      Download the PowerShell upload script, then run it on a machine with the A:\ drive mapped to upload them.
                      After uploading, re-run this tool to get the complete links.
                    </Alert>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Download the template file for reference<br />
                2. Prepare your Excel/CSV with Item IDs in column A<br />
                3. (Optional) Upload images from A:\ drive to S3 first<br />
                4. Upload your file using drag & drop or browse<br />
                5. Click "Process" to start<br />
                6. Results Excel with S3 links will auto-download
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Logic Explanation Dropdown */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: showLogic ? 2 : 0 }}
                onClick={() => setShowLogic(!showLogic)}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                  How the Logic Works ({mode === 'rugs' ? 'Rugs' : 'Furniture'})
                </Typography>
                {showLogic ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>

              <Collapse in={showLogic}>
                {mode === 'rugs' ? (
                  <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                      Primary Image Selection
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      For each item, the tool extracts the <strong>base item</strong> (e.g., "MAD473H") and the <strong>size extension</strong> (e.g., "8"). It first tries to find an exact-match image, then falls back through the size interchange group in order of preference.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Size Interchange Groups
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Each size has a list of interchangeable sizes (tried in order). If the exact size image doesn't exist, the tool uses the next available size from the group:
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 300, overflow: 'auto' }}>
                      <strong>Regular Sizes:</strong><br />
                      3 &rarr; 4<br />
                      4 &rarr; 3, 5<br />
                      5 &rarr; 4, 6, 7<br />
                      6 &rarr; 5, 7, 8<br />
                      7 &rarr; 6, 8, 9, 10, 810, 811...1518<br />
                      8 &rarr; 7, 9, 10, 810, 811...1518<br />
                      9+ &rarr; All large sizes (7-1518) are interchangeable<br /><br />

                      <strong>Round Sizes:</strong><br />
                      3R &rarr; 4R<br />
                      4R &rarr; 3R, 5R<br />
                      5R &rarr; 4R, 6R, 7R<br />
                      6R &rarr; 5R, 7R, 8R<br />
                      7R+ &rarr; 6R-12R interchangeable<br /><br />

                      <strong>Square Sizes:</strong><br />
                      3SQ &rarr; 4SQ<br />
                      4SQ &rarr; 3SQ, 5SQ<br />
                      5SQ &rarr; 4SQ, 6SQ, 7SQ<br />
                      6SQ &rarr; 5SQ, 7SQ, 8SQ<br />
                      7SQ+ &rarr; 6SQ-12SQ interchangeable<br /><br />

                      <strong>Oval Sizes (with Round fallbacks):</strong><br />
                      3OV &rarr; 4OV, 3R, 4R<br />
                      4OV &rarr; 3OV, 5OV, 3R, 4R, 5R<br />
                      5OV &rarr; 4OV, 6OV, 7OV, 4R-7R<br />
                      7OV+ &rarr; 6OV-12OV, 6R-12R<br /><br />

                      <strong>Accent Sizes:</strong><br />
                      2, 23, 24, 25, 240, 250, 2134...2848 &rarr; All interchangeable<br /><br />

                      <strong>Runner Sizes:</strong><br />
                      26, 27, 28, 29, 36-39, 210-222 &rarr; All interchangeable
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Shape Filtering for ROOM Images
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      ROOM, 3DROOM, and other room-scene images are filtered by <strong>both shape and size interchange</strong>. A round item (e.g., -3R) will only use round room images from its interchange group (3R, 4R), not a 7R room image.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Shape Filtering for DETAIL, FOLD, SWATCH, ROOMBACK
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      These image types show material/texture close-ups where size doesn't matter, so they are <strong>exempt from size interchange filtering</strong>. However, they still follow shape group rules:
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                        &bull; <strong>Round + Oval</strong> items can share DETAIL/FOLD/SWATCH/ROOMBACK images with each other (any size)
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                        &bull; <strong>Square + Rectangle</strong> items can share DETAIL/FOLD/SWATCH/ROOMBACK images with each other (any size)
                      </Typography>
                      <Typography variant="body2">
                        &bull; The two groups <strong>cannot cross</strong> &mdash; a round item won't use a rectangle's detail image
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                      Item ID Parsing
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      The tool parses each item ID to determine the <strong>search term</strong> and <strong>suffix</strong>:
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                        &bull; <strong>BED items</strong> (e.g., BED1234-K): full item ID is the search term
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                        &bull; <strong>Other items</strong> (e.g., FOX6000A-K): everything before the last dash is the search term ("FOX6000A"), last segment is the suffix ("-K")
                      </Typography>
                      <Typography variant="body2">
                        &bull; <strong>No dash</strong> (e.g., TBL1234): full ID is the search term, no suffix filtering
                      </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Image Matching
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      The tool finds <strong>all images</strong> in the database whose filename contains the search term (case-insensitive). For "FOX6000A", this matches FOX6000A-K.jpg, FOX6000A-K-ROOM.jpg, FOX6000A-K-DETAIL.jpg, etc.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Suffix Exclusion Filtering (F/Q/K vs T)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Furniture items use bed-size suffixes that must not be mixed:
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                        &bull; Items ending with <strong>-F</strong> (Full), <strong>-Q</strong> (Queen), or <strong>-K</strong> (King): exclude all images containing "-T" in the filename
                      </Typography>
                      <Typography variant="body2">
                        &bull; Items ending with <strong>-T</strong> (Twin): exclude all images containing "-F", "-Q", or "-K" in the filename
                      </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      No Size Interchange
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Unlike rugs, furniture items do <strong>not</strong> have size interchange groups. There is no round/oval/square/rectangle shape filtering. The tool simply finds all images matching the search term and applies suffix exclusion.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                      Image Classification
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Each matched image is classified into an Excel column based on its filename suffix. The classification order (first match wins):
                    </Typography>
                    <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 250, overflow: 'auto' }}>
                      Exact match &rarr; ItemImage<br />
                      LIGHT-ICON &rarr; LIGHT-ICON (design-level: applies to every size of that design)<br />
                      GROUP &rarr; Other1-7<br />
                      FLOOR/FL &rarr; FLOOR<br />
                      LEG &rarr; LEG<br />
                      -ON/-OFF &rarr; ON/OFF<br />
                      COMPOSITE, ARM, CU &rarr; respective columns<br />
                      MAIN1-4, MAIN-1 to MAIN-4, MAIN &rarr; respective columns<br />
                      3DROOMBACK/FRONT/TOP/FLOOR, 3DROOM &rarr; respective columns<br />
                      ROOMAI, ROOMAI2-10 &rarr; respective columns<br />
                      ROOMBACK, ROOMFRONT, ROOMMAIN, ROOMTOP, ROOMFLOOR &rarr; respective columns<br />
                      DETAIL1-10, DETAIL &rarr; respective columns<br />
                      ROOM1-4, WASHROOM, ROOM &rarr; respective columns<br />
                      DIM, FRONT-NS, FRONT, TOP, FOLD &rarr; respective columns<br />
                      BACK1-4, BACK &rarr; respective columns<br />
                      CORNER/CRN/CRNR, CENTER/CEN, SWATCH &rarr; respective columns<br />
                      SIDE variants, OPEN, ALT &rarr; respective columns<br />
                      UGC1-4, UGC, HAND, COLLAPSED, BACKSIDE &rarr; respective columns<br />
                      DOUBLE, PAIR, FROST, HARVEST, ANGLE &rarr; respective columns<br />
                      Special OAK keywords &rarr; Special OAK<br />
                      Everything else &rarr; Other1-7
                    </Box>
                  </Box>
                )}
              </Collapse>
            </Paper>

            {/* "Next steps" — sits directly below the Status & Downloads
                paper in the same right column, so the follow-up tools
                (bulk download / link check) are right under the eye-line
                of the download alert without being inside the panel. */}
            <RelatedToolsBar layout="stack" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AWSImageLinksAlternate;
